import { Controller, useFormContext, useWatch } from "react-hook-form";
import { ArrowRight, Lock } from "lucide-react";
import { JOB_STEPS } from "../CreateJobPage";
import { Checkbox } from "@components/ui/checkbox";
import FieldError from "./FieldError";
import { JobFormValues } from "../schema/jobForm.schema";
import { useCreateJob } from "../hooks/useCreateJobWizard";
import { useCallback, useEffect, useState } from "react";
import { getWalletBalance } from "@endpoints/wallet.service";
import { getJobPostingFee } from "@endpoints/payment.service";
import { useSnackbar } from "notistack";
import { getJobPostingFeeBreakdown, VAT_RATE } from "../lib/jobPostingFee";
import {
  createPaymentIntent,
  getPaymentMethods,
} from "@endpoints/stripe.service";
import { walletPurchase } from "@endpoints/wallet.service";

// Stripe payment method, as returned by payment-methods/card.
interface SavedCard {
  id: string;
  card?: {
    brand?: string;
    last4?: string;
    exp_month?: number;
    exp_year?: number;
  };
  billing_details?: { name?: string };
}

const CARD_BRAND_LABELS: Record<string, string> = {
  visa: "Visa",
  mastercard: "Mastercard",
  amex: "American Express",
  maestro: "Maestro",
};

const getCardLabel = (savedCard?: SavedCard) => {
  const brand = savedCard?.card?.brand?.toLowerCase() ?? "";
  return `${CARD_BRAND_LABELS[brand] ?? "Card"} ending ${savedCard?.card?.last4 ?? "••••"}`;
};

// Amounts are shown from pence; null means not loaded yet.
const formatPounds = (cents: number | null) =>
  cents === null
    ? "—"
    : new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
      }).format(cents / 100);

const Step8 = ({ activeStep }) => {
  const { publishJob, isSaving } = useCreateJob();
  const { enqueueSnackbar } = useSnackbar();
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext<JobFormValues>();
  const [
    title,
    jobPostDurationDays,
    paymentAuthorised,
    paymentSource,
    paymentMethodId,
  ] = useWatch({
    control,
    name: [
      "title",
      "jobPostDurationDays",
      "paymentAuthorised",
      "paymentSource",
      "paymentMethodId",
    ],
  });

  // null while loading or when the balance couldn't be fetched.
  const [walletBalanceCents, setWalletBalanceCents] = useState<number | null>(
    null,
  );
  const [isLoadingBalance, setIsLoadingBalance] = useState(true);
  // Job posting fee in pounds, as returned by the API; null until loaded.
  const [jobPostingFee, setJobPostingFee] = useState<number | null>(null);
  const [cardList, setCardList] = useState<SavedCard[]>([]);
  const [isCardLoading, setIsCardLoading] = useState(true);
  const [isLoadingFee, setIsLoadingFee] = useState(true);

  const fetchWalletBalance = useCallback(async () => {
    try {
      const { data, success } = await getWalletBalance();
      const balanceCents = Number(data?.balanceCents);
      if (success && Number.isFinite(balanceCents)) {
        setWalletBalanceCents(balanceCents);
      }
    } catch {
      setWalletBalanceCents(null);
    } finally {
      setIsLoadingBalance(false);
    }
  }, []);

  const fetchCards = useCallback(async () => {
    try {
      const response = await getPaymentMethods("card");
      if (response?.success) setCardList(response.data ?? []);
    } catch (error) {
      enqueueSnackbar(String(error || "Could not load saved cards"), {
        variant: "error",
      });
    } finally {
      setIsCardLoading(false);
    }
  }, [enqueueSnackbar]);

  const fetchJobPostingFee = useCallback(async () => {
    try {
      const { success, data } = await getJobPostingFee();
      const amount = Number(data?.amount);
      if (success && Number.isFinite(amount) && amount > 0) {
        setJobPostingFee(amount);
      } else {
        enqueueSnackbar("Could not load the job posting fee", {
          variant: "error",
        });
      }
    } catch (error) {
      enqueueSnackbar(String(error || "Could not load the job posting fee"), {
        variant: "error",
      });
    } finally {
      setIsLoadingFee(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchWalletBalance();
    fetchJobPostingFee();
    fetchCards();
  }, [fetchWalletBalance, fetchJobPostingFee, fetchCards]);

  const isWalletSelected = paymentSource === "wallet";
  const selectedCard = cardList.find((card) => card.id === paymentMethodId);

  // Fee + VAT + transaction fee, same for wallet and card.
  const order =
    jobPostingFee === null ? null : getJobPostingFeeBreakdown(jobPostingFee);
  const orderTotalCents = order?.totalCents ?? null;

  // Only the job posting fee is sent to backend, rest of additional charges will be added from the backend
  const handlePayment = () =>
    publishJob(async (savedJobId) => {
      if (isWalletSelected) {
        const { success } = await walletPurchase({
          amountCents: order.feeCents,
          currency: "gbp",
          jobId: savedJobId,
        });
        if (!success) throw new Error("The wallet payment did not go through");
        return;
      }

      if (!paymentMethodId) throw new Error("Select a card to pay with");
      const { success } = await createPaymentIntent(
        jobPostingFee,
        "gbp",
        paymentMethodId,
        savedJobId,
      );
      if (!success) throw new Error("The card payment did not go through");
    });

  const walletCoversOrder =
    orderTotalCents !== null &&
    walletBalanceCents !== null &&
    walletBalanceCents >= orderTotalCents;

  // Default to the wallet only when it covers the whole order, otherwise the
  // first saved card; wait until the fee, balance and cards have loaded.
  useEffect(() => {
    if (isLoadingFee || isLoadingBalance || isCardLoading) return;
    if (walletCoversOrder) {
      setValue("paymentSource", "wallet");
    } else {
      // With no saved cards, validation asks the user to add/select one.
      setValue("paymentSource", "card");
      setValue("paymentMethodId", cardList[0]?.id ?? "");
    }
  }, [
    isLoadingFee,
    isLoadingBalance,
    isCardLoading,
    walletCoversOrder,
    cardList,
    setValue,
  ]);

  const walletDescription = isLoadingBalance
    ? "Checking available balance..."
    : walletBalanceCents === null
      ? "Balance unavailable"
      : `Available balance ${formatPounds(walletBalanceCents)}`;
  const walletContent =
    walletBalanceCents === null
      ? "Wallet can't be used right now"
      : orderTotalCents === null
        ? isLoadingFee
          ? "Calculating order total..."
          : "Order total unavailable"
        : walletCoversOrder
          ? `Use ${formatPounds(orderTotalCents)} today · ${formatPounds(walletBalanceCents - orderTotalCents)} remaining`
          : `${formatPounds(orderTotalCents - walletBalanceCents)} short of the order total`;

  const paymentSources = [
    {
      key: "wallet",
      label: "COMTRACKED virtual wallet",
      desc: walletDescription,
      content: walletContent,
      disabled: !walletCoversOrder,
      isSelected: isWalletSelected,
      onSelect: () => setValue("paymentSource", "wallet"),
    },
    ...cardList.map((savedCard) => ({
      key: savedCard.id,
      label: getCardLabel(savedCard),
      desc: [
        savedCard.billing_details?.name,
        savedCard.card?.exp_month && savedCard.card?.exp_year
          ? `Expires ${String(savedCard.card.exp_month).padStart(2, "0")}/${String(savedCard.card.exp_year).slice(-2)}`
          : null,
      ]
        .filter(Boolean)
        .join(" · "),
      content: "Charge the full order to this card",
      disabled: false,
      isSelected: paymentSource === "card" && paymentMethodId === savedCard.id,
      onSelect: () => {
        setValue("paymentSource", "card");
        setValue("paymentMethodId", savedCard.id, { shouldValidate: true });
      },
    })),
  ];

  return (
    <>
      {/* Header */}
      <div className="flex border-b pb-3">
        <div className="flex flex-col gap-0.5 flex-1">
          <span className="text-xss font-bold text-light-black uppercase">
            Step {String(activeStep).padStart(2, "0")} /{" "}
            {JOB_STEPS[activeStep - 1]?.label}
          </span>
          <span className="font-semibold text-xl">
            {JOB_STEPS[activeStep - 1]?.title}
          </span>
          <span className="text-xss font-medium text-light-black">
            {JOB_STEPS[activeStep - 1]?.subtitle}
          </span>
        </div>
        <span className="self-end text-xss font-bold text-light-black uppercase leading-3">
          {JOB_STEPS[activeStep - 1]?.tagline}
        </span>
      </div>

      {/* Section 1 */}
      <div className="flex p-4 gap-4">
        {/* Col1 */}
        <div className="flex flex-col rounded-md overflow-hidden border flex-1 h-max">
          <div className="flex items-center justify-between px-4 bg-slate-50">
            <div className="flex flex-col justify-center h-14">
              <span className="font-bold text-xss text-light-black uppercase tracking-wider">
                Payment source
              </span>
              <span className="font-semibold text-sm">Choose how to pay</span>
            </div>

            {isLoadingBalance || isLoadingFee ? null : walletCoversOrder ? (
              <div className="font-bold text-xsss rounded-full uppercase bg-green-100 text-green-800 px-2 py-0.5">
                wallet available
              </div>
            ) : (
              <div className="font-bold text-xsss rounded-full uppercase bg-gray-100 text-light-black px-2 py-0.5">
                wallet unavailable
              </div>
            )}
          </div>

          <div className="flex flex-col p-4 border-y gap-3">
            {paymentSources.map((paymentSource) => {
                    const isSelected = paymentSource.isSelected;
                    return (
                      <button
                        key={paymentSource.key}
                        type="button"
                        disabled={paymentSource.disabled}
                        onClick={paymentSource.onSelect}
                        className={`flex items-center border rounded-md p-4 gap-3 disabled:opacity-60 disabled:cursor-not-allowed ${isSelected ? "border-l-[3.5px] border-slate-600 bg-slate-50" : ""}`}
                      >
                        <div
                          className={`flex items-center justify-center rounded-full border w-4 h-4 shrink-0 ${isSelected ? "bg-slate-600" : "border-light-grey"}`}
                        >
                          <div className="h-1.5 w-1.5 rounded-full bg-white" />
                        </div>
                        <div className="flex flex-col items-start gap-0.5">
                          <span className="font-bold text-xs">
                            {paymentSource.label}
                          </span>
                          <span className="text-light-black font-medium text-xss">
                            {paymentSource.desc}
                          </span>
                          <span className="text-light-black font-medium text-xsss">
                            {paymentSource.content}
                          </span>
                        </div>
                      </button>
                    );
                  })}

            {isCardLoading ? (
              <span className="text-xss font-medium text-light-black">
                Loading saved cards...
              </span>
            ) : cardList.length ? null : (
              <span className="text-xss font-medium text-light-black">
                No saved cards. Add a card from your payment methods.
              </span>
            )}
            <FieldError message={errors.paymentMethodId?.message} />

            <button className="font-bold text-xss text-content-muted underline text-left">
              Manage wallet and payment methods
            </button>
          </div>

          <div className="flex gap-2 bg-slate-100 p-4">
            <Lock size={19} color="gray" />
            <div className="flex flex-col">
              <span className="font-semibold text-xss">
                Wallet funds will be applied securely.
              </span>
              <span className="font-medium text-light-black text-xss">
                Refund credits in the agency wallet can be used toward this job
                post.
              </span>
            </div>
          </div>
        </div>

        {/* Col2 */}
        <div className="flex flex-col border rounded-md overflow-hidden flex-1">
          <div className="flex items-center justify-between h-14 px-4 bg-slate-50">
            <span className="font-bold text-xss text-light-black uppercase tracking-wider">
              order summary
            </span>
          </div>

          <div className="flex items-center justify-between p-4 border-y">
            <div className="flex flex-col">
              <span className="font-semibold text-sm">{title}</span>
              <span className="font-medium text-xsss text-light-black">
                {jobPostDurationDays ?? 30}-day published job post
              </span>
            </div>
            <span className="font-bold text-xs">
              {formatPounds(order?.feeCents ?? null)}
            </span>
          </div>

          <div className="flex flex-col p-4 gap-1.5">
            <div className="flex items-center justify-between">
              <span className="font-medium text-xss text-light-black">
                Job posting fee
              </span>
              <span className="font-bold text-xss text-content-muted">
                {formatPounds(order?.feeCents ?? null)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="font-medium text-xss text-light-black">
                VAT ({VAT_RATE * 100}%)
              </span>
              <span className="font-bold text-xss text-content-muted">
                {formatPounds(order?.vatCents ?? null)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="font-medium text-xss text-light-black">
                Transaction fee
              </span>
              <span className="font-bold text-xss text-content-muted">
                {formatPounds(order?.transactionFeeCents ?? null)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="font-medium text-xss text-light-black">
                Payment source
              </span>
              <span className="font-bold text-xss text-content-muted">
                {isWalletSelected
                  ? "Virtual wallet"
                  : selectedCard
                    ? getCardLabel(selectedCard)
                    : "—"}
              </span>
            </div>
            <div className="flex items-center justify-between border-t pt-1.5 text-black">
              <span className="font-bold text-xs">Total due today</span>
              <span className="font-bold text-xs">
                {formatPounds(orderTotalCents)}
              </span>
            </div>
          </div>

          <div className="flex flex-col p-4 border-t">
            <div className="flex gap-3">
              <Controller
                control={control}
                name="paymentAuthorised"
                render={({ field }) => (
                  <Checkbox
                    id="payment-confirmation"
                    checked={field.value}
                    onCheckedChange={(checked) =>
                      field.onChange(checked === true)
                    }
                  />
                )}
              />
              <label
                htmlFor="payment-confirmation"
                className="font-medium text-xss text-light-black cursor-pointer"
              >
                I authorise COMTRACKED to{" "}
                {isWalletSelected
                  ? `apply ${formatPounds(orderTotalCents)} from the agency virtual wallet`
                  : `charge ${formatPounds(orderTotalCents)} to ${selectedCard ? getCardLabel(selectedCard) : "the selected card"}`}{" "}
                and publish this job post for candidates to view and match
                against.
              </label>
            </div>
            <FieldError message={errors.paymentAuthorised?.message} />
            <button
              type="button"
              onClick={handlePayment}
              // No payment until the real order total is known.
              disabled={isSaving || orderTotalCents === null}
              className={`flex items-center justify-between rounded-md border font-bold text-xss h-10 px-3 my-3 disabled:opacity-50 ${paymentAuthorised ? "bg-black text-white" : "bg-gray-200 text-light-black"}`}
            >
              {isSaving
                ? "Saving..."
                : orderTotalCents === null
                  ? isLoadingFee
                    ? "Loading order total..."
                    : "Order total unavailable"
                  : `Pay ${formatPounds(orderTotalCents)} ${isWalletSelected ? "from wallet" : "by card"} and publish`}
              <ArrowRight size={14} />
            </button>

            <span className="font-medium text-xsss text-light-black">
              The wallet is selected automatically when its available balance
              covers the full order. Split payments are not included in this
              prototype.
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default Step8;
